import { autocompleteClasses } from '@mui/material/Autocomplete';
import Chip, { chipClasses } from '@mui/material/Chip';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  FIELD_ADORNMENT_GAP_PX,
  FIELD_ICON_PX,
  FIELD_PADDING_PX,
} from 'theme/components/field-geometry';
import {
  SELECT_CHIP_BORDER_PX,
  SELECT_CHIP_GEOMETRY,
  SELECT_DIVIDER_WIDTH_PX,
  SELECT_ICON_TARGET_PX,
  SELECT_MULTI_INSET_PX,
  SELECT_TRAILING_GAP_PX,
  SELECT_VALUE_GAP_PX,
} from 'ui/select/select.constants';
import type { SelectSize } from 'ui/select/select.types';

interface FieldSizeOwnerState {
  fieldSize: SelectSize;
}

const shouldForwardProp = (prop: PropertyKey) => prop !== 'fieldSize';

const CONTROL = `.${outlinedInputClasses.root}.${outlinedInputClasses.root}`;
const INPUT = `.${autocompleteClasses.input}`;
const TAG = `.${autocompleteClasses.tag}`;
const END_ADORNMENT = `.${autocompleteClasses.endAdornment}`;
const CLEAR = `.${autocompleteClasses.clearIndicator}`;
const POPUP = `.${autocompleteClasses.popupIndicator}`;
const HAS_CLEAR = `.${autocompleteClasses.hasClearIcon}`;
const HAS_POPUP = `.${autocompleteClasses.hasPopupIcon}`;

const inputPadding = (size: SelectSize, multiple: boolean) => {
  const { x, y } = FIELD_PADDING_PX[size];
  const inset = multiple ? SELECT_MULTI_INSET_PX : 0;

  return { padding: `${y - inset}px 0 ${y - inset}px ${x - inset}px` };
};

export const getSelectControlSx =
  (size: SelectSize, multiple: boolean): SxProps<Theme> =>
  (theme) => {
    const { x } = FIELD_PADDING_PX[size];
    const icon = FIELD_ICON_PX[size];
    const iconInset = (SELECT_ICON_TARGET_PX - icon) / 2;
    const reserve = (iconsWidth: number) =>
      x + FIELD_ADORNMENT_GAP_PX[size] + iconsWidth;
    const bothIconsWidth =
      icon * 2 + SELECT_TRAILING_GAP_PX * 2 + SELECT_DIVIDER_WIDTH_PX;
    const multipleControl = multiple
      ? {
          padding: `${SELECT_MULTI_INSET_PX}px`,
          gap: `${SELECT_VALUE_GAP_PX}px`,
        }
      : { padding: 0 };

    return {
      [`& ${CONTROL}`]: {
        ...multipleControl,
        paddingRight: `${x}px`,
        [`& ${INPUT}`]: inputPadding(size, multiple),
        [`& ${TAG}`]: { margin: 0, maxWidth: '100%' },
        [`& ${END_ADORNMENT}`]: {
          right: x,
          display: 'flex',
          alignItems: 'center',
          gap: `${SELECT_TRAILING_GAP_PX}px`,
        },
        [`& ${CLEAR}, & ${POPUP}`]: {
          padding: `${iconInset}px`,
          margin: `-${iconInset}px`,
          '& svg': { width: icon, height: icon },
        },
        [`& ${CLEAR}`]: { order: 0, color: theme.palette.grey[700] },
        [`& ${POPUP}`]: { order: 2, color: theme.palette.secondary.main },
      },
      [`&${HAS_POPUP} ${CONTROL}, &${HAS_CLEAR} ${CONTROL}`]: {
        paddingRight: `${reserve(icon)}px`,
      },
      [`&${HAS_POPUP}${HAS_CLEAR} ${CONTROL}`]: {
        paddingRight: `${reserve(bothIconsWidth)}px`,
        [`& ${END_ADORNMENT}::before`]: {
          content: '""',
          order: 1,
          width: `${SELECT_DIVIDER_WIDTH_PX}px`,
          height: icon,
          backgroundColor: theme.palette.divider,
        },
      },
    };
  };

export const SelectChip = styled(Chip, {
  shouldForwardProp,
})<FieldSizeOwnerState>(({ theme, fieldSize }) => {
  const chip = SELECT_CHIP_GEOMETRY[fieldSize];
  const isLarge = fieldSize === 'large';

  return {
    ...(isLarge
      ? theme.typography.body2
      : {
          ...theme.typography.caption,
          fontWeight: theme.typography.fontWeightRegular,
        }),
    height: 'auto',
    gap: chip.gap,
    padding: `${chip.paddingY - SELECT_CHIP_BORDER_PX}px ${
      chip.paddingX - SELECT_CHIP_BORDER_PX
    }px`,
    border: `${SELECT_CHIP_BORDER_PX}px solid ${theme.palette.secondary.light}`,
    borderRadius: chip.radius,
    backgroundColor: isLarge
      ? theme.palette.action.hover
      : theme.palette.background.paper,
    color: theme.palette.secondary.main,
    [`& .${chipClasses.label}`]: { padding: 0 },
    [`& .${chipClasses.deleteIcon}`]: {
      width: chip.icon,
      height: chip.icon,
      margin: 0,
      flexShrink: 0,
      color: theme.palette.secondary.main,
      '&:hover': { color: theme.palette.secondary.dark },
    },
    [`&.${chipClasses.disabled}`]: {
      opacity: 1,
      borderColor: theme.palette.action.disabledBorder,
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
    [`&.${chipClasses.disabled} .${chipClasses.deleteIcon}`]: {
      color: theme.palette.text.disabled,
    },
  };
});

export const SelectVisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
